<template>
  <q-dialog
    ref="dialog"
    v-model="shown"
  >
    <q-card class="q-dialog-plugin">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          <q-icon
            name="credit_card"
            class="q-mr-sm"
          />
          Gift Card Recharge
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div class="text-caption text-grey-6 q-mb-md">
          Add funds to an existing gift card
        </div>

        <!-- Gift Card Selection -->
        <div v-if="!selectedGiftCard">
          <div class="text-subtitle2 text-weight-medium q-mb-sm">
            <q-icon
              name="credit_card"
              class="q-mr-xs"
            />
            Select Gift Card
          </div>

          <div
            v-if="availableGiftCards.length === 0"
            class="text-center q-py-xl"
          >
            <q-icon
              name="credit_card_off"
              size="48px"
              color="grey-4"
              class="q-mb-md"
            />
            <div class="text-grey-6">
              No gift cards available
            </div>
            <div class="text-caption text-grey-5">
              Add a gift card to the order first
            </div>
          </div>

          <div
            v-else
            class="row q-col-gutter-sm"
          >
            <div
              v-for="giftCard in availableGiftCards"
              :key="giftCard.id"
              class="col-12 col-sm-6 col-md-4"
            >
              <q-card
                flat
                bordered
                class="cursor-pointer gift-card-item"
                :class="{ 'bg-primary text-white': selectedGiftCard?.id === giftCard.id }"
                @click="selectGiftCard(giftCard)"
              >
                <q-card-section class="q-pa-md text-center">
                  <q-icon
                    name="credit_card"
                    size="24px"
                    class="q-mb-sm"
                    :color="selectedGiftCard?.id === giftCard.id ? 'white' : 'primary'"
                  />
                  <div class="text-caption">
                    Current Balance
                  </div>
                  <i18n-n
                    class="text-h6 text-weight-bold"
                    tag="div"
                    :value="giftCard.balance"
                    format="currency"
                    scope="global"
                  />
                </q-card-section>
              </q-card>
            </div>
          </div>
        </div>

        <!-- Recharge Form -->
        <div v-else>
          <div class="text-subtitle2 text-weight-medium q-mb-sm">
            <q-icon
              name="add_circle"
              class="q-mr-xs"
            />
            Recharge Amount
          </div>

          <!-- Selected Gift Card Info -->
          <q-card
            flat
            bordered
            class="bg-grey-1 q-mb-md"
          >
            <q-card-section class="q-pa-sm">
              <div class="row items-center q-gutter-sm">
                <q-icon
                  name="credit_card"
                  color="primary"
                  size="sm"
                />
                <div class="col">
                  <div class="text-weight-medium">
                    {{ selectedGiftCard.humanUid }}
                  </div>
                  <div class="text-caption text-grey-6">
                    Current Balance:
                    <i18n-n
                      tag="span"
                      :value="selectedGiftCard.balance"
                      format="currency"
                      scope="global"
                    />
                  </div>
                </div>
                <q-btn
                  flat
                  round
                  icon="edit"
                  size="sm"
                  @click="selectedGiftCard = null"
                >
                  <q-tooltip>Change card</q-tooltip>
                </q-btn>
              </div>
            </q-card-section>
          </q-card>

          <!-- Amount Input -->
          <q-input
            v-model.number="amount"
            label="Recharge Amount"
            type="number"
            min="0"
            max="1000"
            step="1"
            outlined
            dense
            :rules="[
              val => val > 0 || 'Amount must be greater than 0',
              val => val <= 1000 || 'Maximum amount is $1,000'
            ]"
          >
            <template #prepend>
              <q-icon name="attach_money" />
            </template>
            <template #append>
              <q-btn
                flat
                dense
                label="Clear"
                @click="amount = null"
              />
            </template>
          </q-input>

          <!-- Quick Amount Buttons -->
          <div class="q-mt-md">
            <div class="text-caption text-grey-6 q-mb-sm">
              Quick amounts
            </div>
            <div class="row q-col-gutter-xs">
              <div
                v-for="quickAmount in [10, 25, 50, 100]"
                :key="quickAmount"
                class="col"
              >
                <q-btn
                  flat
                  :label="`$${quickAmount}`"
                  :color="amount === quickAmount ? 'primary' : 'grey-7'"
                  :outline="amount !== quickAmount"
                  size="sm"
                  @click="amount = quickAmount"
                />
              </div>
            </div>
          </div>

          <!-- Preview -->
          <div
            v-if="amount"
            class="q-mt-md"
          >
            <q-card
              flat
              bordered
              class="bg-blue-1"
            >
              <q-card-section class="q-pa-sm">
                <div class="row items-center q-gutter-sm">
                  <q-icon
                    name="info"
                    color="primary"
                    size="sm"
                  />
                  <div class="col">
                    <div class="text-caption text-primary">
                      Recharge Preview
                    </div>
                    <div class="text-weight-medium">
                      Adding
                      <i18n-n
                        tag="span"
                        :value="amount"
                        format="currency"
                        scope="global"
                      />
                      to gift card
                    </div>
                    <div class="text-caption text-grey-6">
                      New balance:
                      <i18n-n
                        tag="span"
                        :value="selectedGiftCard.balance + amount"
                        format="currency"
                        scope="global"
                      />
                    </div>
                  </div>
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>
      </q-card-section>

      <q-card-actions class="q-pa-md">
        <q-btn
          flat
          label="Cancel"
          @click="hide"
        />
        <q-space />
        <q-btn
          v-if="selectedGiftCard"
          color="primary"
          label="Add Recharge"
          :disabled="!amount || amount <= 0"
          :loading="false"
          @click="recharge"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>

import { ORDER_ITEM_SUBTYPES, ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'

export default {
  name: 'RegisterButtonGiftCardRechargeDialog',
  inject: ['order'],
  data () {
    return {
      selectedGiftCard: null,
      amount: null,
      shown: false
    }
  },
  computed: {
    availableGiftCards () {
      return this.order.giftCards.data
    }
  },
  methods: {
    selectGiftCard (giftCard) {
      this.selectedGiftCard = giftCard
    },
    hide () {
      this.shown = false
      this.resetFields()
    },
    resetFields () {
      this.selectedGiftCard = null
      this.amount = null
    },
    show () {
      this.shown = true
      this.resetFields()
    },
    recharge () {
      this.order.orderItems.add({
        type: ORDER_ITEM_TYPES.GIFT_CARD,
        subtype: ORDER_ITEM_SUBTYPES.GIFT_CARD_RECHARGE,
        giftCard: this.selectedGiftCard,
        amount: this.amount
      })
      this.hide()
    }
  }
}
</script>

<style scoped>
.gift-card-item {
  transition: all 0.2s ease;
}

.gift-card-item:active {
  transform: translateY(0);
}
</style>
